import React, { useEffect, useState } from 'react'
import type { CSSProperties } from 'react'
import {
  Drawer,
  DrawerNavigation,
  DrawerContent,
} from '@progress/kendo-react-layout'
import i18n from 'i18next'
import AppVersion from '../matteroomTab/AppVersion'
import DECO from '../../public/assets/deco.svg'
import SIGNOUT_ICON from '../../public/assets/signout_icon.svg'
import MIRA_LOGO from '../../public/assets/mobile/side_menu_logo.svg'
import CHART_ICON from '../../public/assets/mobile/chart_icon.svg'
import { Switch } from '@progress/kendo-react-inputs'
import { handleLogout } from '../features/auth/utils/logout'
import { LOCAL_AUTH_STORAGE_KEYS } from '../features/auth/constants'
import { isTeamsApp } from '../constants'
import { useDeviceStore } from '../stores/useDeviceStore'

export type MRDrawerItem = {
  type: 'default' | 'tools'
  order: number
  text: string
  icon: string
  selected_icon: string
  selected?: boolean
  logo?: string
}

type MRDrawerProps = {
  items?: MRDrawerItem[]
  selectedId: number
  timeEntriesCount?: number | null
  expanded: boolean
  onSelect: (text: string, order: number) => void
  handleToggleDrawer: () => void
  children?: any
  onChatbotToggle?: (enabled: boolean) => void
}

const MRDrawer = ({
  items = [],
  selectedId,
  timeEntriesCount = null,
  expanded,
  onSelect,
  handleToggleDrawer,
  children,
  onChatbotToggle,
}: MRDrawerProps) => {
  const [chatbotEnabled, setChatbotEnabled] = useState(false)
  const isMobile = useDeviceStore(state => state.isMobile)
  const defaultItems = items.filter(item => item.type === 'default')
  const toolItems = isMobile
    ? items.filter(item => item.type === 'tools' && item.text !== 'settings')
    : items.filter(item => item.type === 'tools')

  const navItemStyles: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  }

  useEffect(() => {
    if (onChatbotToggle) {
      onChatbotToggle(chatbotEnabled)
    }
  }, [])

  const handleSignOut = () => {
    handleLogout()
    if (isTeamsApp) {
      localStorage.setItem(LOCAL_AUTH_STORAGE_KEYS.showBreak, 'true')
      window.location.reload()
    } else {
      window.location.href = '/'
    }
  }

  return (
    <Drawer
      expanded={expanded}
      position="start"
      mode={isMobile ? 'overlay' : 'push'}
      onOverlayClick={handleToggleDrawer}
    >
      {!isMobile && expanded && (
        <button
          className="btn-close-panel appearance-none border-0"
          onClick={handleToggleDrawer}
          style={{
            position: 'absolute',
            top: 25,
            left: 228,
            cursor: 'pointer',
            zIndex: 9999,
          }}
        >
          <img />
        </button>
      )}

      <DrawerNavigation
        style={{ height: '100%', width: isMobile ? '300px' : '240px' }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            height: '100%',
          }}
        >
          <div style={{ paddingTop: isMobile ? '60px' : 0 }}>
            {/* header icon */}
            {isMobile && (
              <img
                src={MIRA_LOGO}
                style={{ padding: '0.5rem 1rem', width: '25vw' }}
              />
            )}
            <ul className="k-drawer-items">
              {defaultItems.map(item => (
                <li
                  key={item.order}
                  className={
                    item.order === selectedId
                      ? 'k-drawer-item k-selected'
                      : 'k-drawer-item'
                  }
                  onClick={() => onSelect(item.text, item.order)}
                >
                  <div style={navItemStyles}>
                    <img
                      style={{ marginRight: 10 }}
                      src={
                        item.logo ||
                        (item.order === selectedId
                          ? item.selected_icon
                          : item.icon)
                      }
                    />
                    {item.text === 'reports' ? (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          flex: 1,
                        }}
                      >
                        <span>{i18n.t('actions.' + item.text)}</span>
                        {/* <div style={{ borderRadius: 5, background: '#97A3B0', padding: '0px 5px', color: '#fff', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Beta</div> */}
                      </div>
                    ) : (
                      <>
                        {i18n.t('actions.' + item.text)}
                        {item.text === 'timeEntries' &&
                        typeof timeEntriesCount === 'number' &&
                        timeEntriesCount > 0
                          ? ` (${timeEntriesCount})`
                          : ''}
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>

            {/* Tool Items */}
            {!isMobile && (
              <div
                style={{
                  padding: '20px 1rem 0.5rem 1rem',
                  fontSize: '14px',
                  fontWeight: 400,
                  color: '#999',
                }}
              >
                {i18n.t('labels.tools')}
              </div>
            )}
            {isMobile && (
              <div
                style={{ margin: '0 1rem', border: '1px solid #E0DEDD' }}
              ></div>
            )}
            <ul className="k-drawer-items">
              {toolItems.map(item => (
                <li
                  key={item.order}
                  className={
                    item.order === selectedId
                      ? 'k-drawer-item k-selected'
                      : 'k-drawer-item'
                  }
                  onClick={() => onSelect(item.text, item.order)}
                >
                  <div style={navItemStyles}>
                    <img
                      style={{ marginRight: 10 }}
                      src={
                        item.order === selectedId
                          ? item.selected_icon
                          : item.icon
                      }
                    />
                    {i18n.t('actions.' + item.text)}
                  </div>
                </li>
              ))}
              <li key="chatbot" className="k-drawer-item">
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <img style={{ marginRight: 10 }} src={CHART_ICON} />
                    {'Chatbot'}
                  </div>

                  <Switch
                    checked={chatbotEnabled}
                    onChange={e => {
                      setChatbotEnabled(e.value)
                      if (onChatbotToggle) {
                        onChatbotToggle(e.value)
                      }
                    }}
                    onLabel={'ON'}
                    offLabel={'OFF'}
                  />
                </div>
              </li>
            </ul>
          </div>

          <div style={{ marginTop: '40px' }}>
            <div style={{ padding: '0.5rem 1rem' }}>
              <div
                style={{
                  position: 'relative',
                  borderRadius: '5px',
                  background: '#F2F4FF',
                  marginBottom: '10px',
                  padding: '40px 15px 15px 15px',
                }}
              >
                <img
                  style={{ position: 'absolute', top: '-34px' }}
                  src={DECO}
                />
                <div
                  style={{
                    color: '#865BDF',
                    fontSize: '14px',
                    fontWeight: 400,
                  }}
                >
                  {i18n.t('login.miraConfigured')}
                </div>
              </div>
              <AppVersion />
            </div>
            <div
              className="nav-item"
              style={{ borderTop: '1px solid #ddd' }}
              onClick={handleSignOut}
            >
              <img src={SIGNOUT_ICON} />
              {i18n.t('actions.signout')}
            </div>
          </div>
        </div>
      </DrawerNavigation>

      <DrawerContent
        style={{
          width: isMobile ? '100%' : 'calc(100% - 240px)',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          background: '#fff',
        }}
      >
        {children}
      </DrawerContent>
    </Drawer>
  )
}

export default MRDrawer
